import React, {
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";

import { useNavigate } from "react-router-dom";
import { gsap } from "gsap";
import axios from "axios";
import YouTube from "react-youtube";
import { useLanguage } from "../contexts/LanguageContext";


const normalizeAngle = (angle) => {
    return (
        ((angle + 180) % 360 + 360) % 360 - 180
    );
};

const SuperStarVideoGallery = ({
    actorName = "",
    movies = [],
    regionalScope = null,
    onBackdropSelect,
    heroBackdropCue = 0,
}) => {
    const { t } = useLanguage();
    const stageRef = useRef(null);
    const ringRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const suppressClickRef = useRef(false);
    const activeIndexRef = useRef(0);
    const navigate = useNavigate();
    const videoCacheRef = useRef(new Map());

    // MOVIEFRENZY — indicateur visuel annonçant
    // qu'un nouveau Hero Backdrop vient d'apparaître plus haut.
    const [showHeroBackdropCue, setShowHeroBackdropCue] = useState(false);

    const [activeVideo, setActiveVideo] = useState({
        movieId: null,
        key: null,
    });

    const [readyVideoMovieId, setReadyVideoMovieId] =
        useState(null);

    const activeMovie = movies[activeIndex] || null;
    const activeMovieId = activeMovie?.id || null;

    const [settledMovieId, setSettledMovieId] =
        useState(null);

    const [isGallerySettled, setIsGallerySettled] =
        useState(false);

    const gallerySettledRef = useRef(false);

    const [hoveredBackdropMovie, setHoveredBackdropMovie] = useState(null);
    const [focusedBackdropMovie, setFocusedBackdropMovie] = useState(null);
    const [touchBackdropEnabled, setTouchBackdropEnabled] = useState(false);

    const backdropCandidate = hoveredBackdropMovie || focusedBackdropMovie || (
        touchBackdropEnabled && isGallerySettled ? activeMovie : null
    );

    // ------------------------------------------------------
    // HERO BACKDROP CUE
    //
    // Lorsque ActorHeroBackdrop confirme qu'une nouvelle
    // image est réellement chargée, les flèches apparaissent
    // brièvement pour indiquer que quelque chose vient
    // de changer plus haut dans la page.
    // ------------------------------------------------------

    useEffect(() => {
        if (!heroBackdropCue) return undefined;

        setShowHeroBackdropCue(true);

        const timer = window.setTimeout(() => {
            setShowHeroBackdropCue(false);
        }, 2200);

        return () => {
            window.clearTimeout(timer);
        };
    }, [heroBackdropCue]);

    useEffect(() => {
        if (!backdropCandidate || !onBackdropSelect) return undefined;

        // Seules deux secondes continues valident une nouvelle sélection.
        const timer = window.setTimeout(() => {
            if (!document.hidden) onBackdropSelect(backdropCandidate);
        }, 2000);

        return () => window.clearTimeout(timer);
    }, [backdropCandidate, onBackdropSelect]);

    useEffect(() => {
        const resetBackdropIntent = () => {
            setHoveredBackdropMovie(null);
            setFocusedBackdropMovie(null);
            setTouchBackdropEnabled(false);
        };
        resetBackdropIntent();
        window.addEventListener("blur", resetBackdropIntent);
        document.addEventListener("visibilitychange", resetBackdropIntent);
        return () => {
            window.removeEventListener("blur", resetBackdropIntent);
            document.removeEventListener("visibilitychange", resetBackdropIntent);
        };
    }, [movies]);

    useLayoutEffect(() => {
        if (
            !stageRef.current ||
            !ringRef.current ||
            movies.length < 2
        ) {
            return undefined;
        }

        const mm = gsap.matchMedia();

        mm.add("(min-width: 901px)", () => {
            const stage = stageRef.current;
            const ring = ringRef.current;

            const angleStep = 360 / movies.length;

            // ------------------------------------------------------
            // Détection de la carte actuellement face caméra
            // ------------------------------------------------------

            const updateActiveMovie = () => {
                const ringRotation =
                    Number(gsap.getProperty(ring, "rotationY")) || 0;

                let nextActiveIndex = 0;
                let smallestDistance = Infinity;

                movies.forEach((movie, index) => {
                    const movieAngle =
                        index * angleStep + ringRotation;

                    const normalizedMovieAngle =
                        normalizeAngle(movieAngle);

                    const distanceFromCamera =
                        Math.abs(normalizedMovieAngle);

                    if (distanceFromCamera < smallestDistance) {
                        smallestDistance = distanceFromCamera;
                        nextActiveIndex = index;
                    }
                });

                if (
                    nextActiveIndex !== activeIndexRef.current
                ) {
                    activeIndexRef.current = nextActiveIndex;
                    setActiveIndex(nextActiveIndex);
                }
            };

            // ------------------------------------------------------
            // Position initiale
            // ------------------------------------------------------

            gsap.set(ring, {
                rotationY: 0,
            });

            // ------------------------------------------------------
            // MOVIEFRENZY — pilotage par proximité souris
            // ------------------------------------------------------

            let targetVelocity = 0;
            let currentVelocity = 0;
            let snapTween = null;

            const MAX_SPEED = 77; // Je me décide jamais èa chaque jour ! 😅
            const DEAD_ZONE = 0.18;
            const SMOOTHING = 0.08;

            const handlePointerMove = (event) => {
                const rect = stage.getBoundingClientRect();

                if (!rect.width) return;

                const localX = gsap.utils.clamp(
                    0,
                    rect.width,
                    event.clientX - rect.left
                );

                // -1 = extrême gauche
                //  0 = centre
                // +1 = extrême droite
                const normalizedX = gsap.utils.mapRange(
                    0,
                    rect.width,
                    -1,
                    1,
                    localX
                );

                const distanceFromCenter =
                    Math.abs(normalizedX);

                // Zone centrale = arrêt demandé
                if (distanceFromCenter <= DEAD_ZONE) {
                    targetVelocity = 0;
                    return;
                }

                // Plus on approche du bord,
                // plus la vitesse augmente.
                const intensity = gsap.utils.mapRange(
                    DEAD_ZONE,
                    1,
                    0,
                    1,
                    distanceFromCenter
                );

                targetVelocity =
                    -Math.sign(normalizedX) *
                    MAX_SPEED *
                    intensity;
            };

            const handlePointerLeave = () => {
                targetVelocity = 0;
            };

            // ------------------------------------------------------
            // Animation continue avec inertie
            // ------------------------------------------------------

            const updateGallery = (_time, deltaTime) => {
                currentVelocity +=
                    (targetVelocity - currentVelocity) *
                    SMOOTHING;

                if (
                    targetVelocity === 0 &&
                    Math.abs(currentVelocity) < 0.01
                ) {
                    currentVelocity = 0;
                }

                const nextGallerySettled =
                    targetVelocity === 0 &&
                    Math.abs(currentVelocity) < 0.15;

                if (
                    nextGallerySettled !==
                    gallerySettledRef.current
                ) {
                    gallerySettledRef.current =
                        nextGallerySettled;

                    setIsGallerySettled(
                        nextGallerySettled
                    );

                    if (nextGallerySettled) {
                        const currentRotation =
                            Number(
                                gsap.getProperty(
                                    ring,
                                    "rotationY"
                                )
                            ) || 0;

                        const snappedRotation =
                            Math.round(
                                currentRotation / angleStep
                            ) * angleStep;

                        snapTween?.kill();

                        snapTween = gsap.to(ring, {
                            rotationY: snappedRotation,
                            duration: 0.32,
                            ease: "power2.out",
                            onUpdate: updateActiveMovie,
                        });
                    }
                }

                if (currentVelocity !== 0) {
                    const currentRotation =
                        Number(
                            gsap.getProperty(
                                ring,
                                "rotationY"
                            )
                        ) || 0;

                    const deltaSeconds =
                        deltaTime / 1000;

                    gsap.set(ring, {
                        rotationY:
                            currentRotation +
                            currentVelocity *
                            deltaSeconds,
                    });
                }

                updateActiveMovie();
            };

            // ------------------------------------------------------
            // Events
            // ------------------------------------------------------

            stage.addEventListener(
                "pointermove",
                handlePointerMove
            );

            stage.addEventListener(
                "pointerleave",
                handlePointerLeave
            );

            gsap.ticker.add(updateGallery);

            updateActiveMovie();

            // ------------------------------------------------------
            // Cleanup
            // ------------------------------------------------------

            return () => {
                stage.removeEventListener(
                    "pointermove",
                    handlePointerMove
                );

                stage.removeEventListener(
                    "pointerleave",
                    handlePointerLeave
                );

                gsap.ticker.remove(updateGallery);
                snapTween?.kill();
                gsap.killTweensOf(ring);
            };
        });

        // Bloc pour mobile
        mm.add("(max-width: 900px)", () => {
            const stage = stageRef.current;
            const ring = ringRef.current;

            const angleStep = 360 / movies.length;

            let isDragging = false;
            let pointerId = null;

            let startX = 0;
            let startRotation = 0;

            let snapTween = null;

            let lastX = 0;
            let lastMoveTime = 0;
            let swipeVelocity = 0;

            const INERTIA_DURATION_MS = 220;
            const MAX_INERTIA_STEPS = 2;

            // Sensibilité du déplacement tactile.
            // Plus la valeur monte, plus le carrousel tourne vite
            // pour un même déplacement du doigt.
            const DRAG_DEGREES_PER_PX = 0.32;

            const updateActiveMovie = () => {
                const ringRotation =
                    Number(gsap.getProperty(ring, "rotationY")) || 0;

                let nextActiveIndex = 0;
                let smallestDistance = Infinity;

                movies.forEach((movie, index) => {
                    const movieAngle =
                        index * angleStep + ringRotation;

                    const normalizedMovieAngle =
                        normalizeAngle(movieAngle);

                    const distanceFromCamera =
                        Math.abs(normalizedMovieAngle);

                    if (distanceFromCamera < smallestDistance) {
                        smallestDistance = distanceFromCamera;
                        nextActiveIndex = index;
                    }
                });

                if (
                    nextActiveIndex !== activeIndexRef.current
                ) {
                    activeIndexRef.current = nextActiveIndex;
                    setActiveIndex(nextActiveIndex);
                }
            };

            const setGallerySettled = (value) => {
                gallerySettledRef.current = value;
                setIsGallerySettled(value);
            };

            // Position initiale mobile
            gsap.set(ring, {
                rotationY: 0,
            });

            updateActiveMovie();
            setGallerySettled(true);

            const handlePointerDown = (event) => {
                // Le bloc mobile doit répondre au tactile / stylet,
                // pas à une souris réelle.
                if (event.pointerType === "mouse") return;

                isDragging = true;
                pointerId = event.pointerId;

                startX = event.clientX;
                startRotation =
                    Number(
                        gsap.getProperty(ring, "rotationY")
                    ) || 0;

                lastX = event.clientX;
                lastMoveTime = event.timeStamp;
                swipeVelocity = 0;

                suppressClickRef.current = false;

                snapTween?.kill();
                setGallerySettled(false);

                if (stage.setPointerCapture) {
                    stage.setPointerCapture(event.pointerId);
                }
            };

            const handlePointerMove = (event) => {
                if (
                    !isDragging ||
                    event.pointerId !== pointerId
                ) {
                    return;
                }

                const deltaX =
                    event.clientX - startX;

                const deltaTime =
                    event.timeStamp - lastMoveTime;

                if (deltaTime > 0) {
                    const instantVelocity =
                        (event.clientX - lastX) / deltaTime;

                    swipeVelocity =
                        swipeVelocity * 0.65 +
                        instantVelocity * 0.35;
                }

                lastX = event.clientX;
                lastMoveTime = event.timeStamp;

                // On distingue un vrai swipe d'un simple tap.
                if (Math.abs(deltaX) > 7) {
                    suppressClickRef.current = true;
                }

                gsap.set(ring, {
                    rotationY:
                        startRotation +
                        deltaX * DRAG_DEGREES_PER_PX,
                });

                updateActiveMovie();
            };

            const finishPointerGesture = (event) => {
                if (
                    !isDragging ||
                    event.pointerId !== pointerId
                ) {
                    return;
                }

                isDragging = false;

                if (
                    stage.releasePointerCapture &&
                    stage.hasPointerCapture?.(event.pointerId)
                ) {
                    stage.releasePointerCapture(
                        event.pointerId
                    );
                }

                const currentRotation =
                    Number(
                        gsap.getProperty(ring, "rotationY")
                    ) || 0;

                // Si le doigt est resté immobile un instant avant
                // d'être relâché, on annule l'élan résiduel.
                const timeSinceLastMove =
                    event.timeStamp - lastMoveTime;

                if (timeSinceLastMove > 80) {
                    swipeVelocity = 0;
                }

                const rawInertiaDegrees =
                    swipeVelocity *
                    INERTIA_DURATION_MS *
                    DRAG_DEGREES_PER_PX;

                const maxInertiaDegrees =
                    angleStep * MAX_INERTIA_STEPS;

                const inertiaDegrees =
                    gsap.utils.clamp(
                        -maxInertiaDegrees,
                        maxInertiaDegrees,
                        rawInertiaDegrees
                    );

                const projectedRotation =
                    currentRotation + inertiaDegrees;

                const snappedRotation =
                    Math.round(
                        projectedRotation / angleStep
                    ) * angleStep;

                snapTween?.kill();

                snapTween = gsap.to(ring, {
                    rotationY: snappedRotation,
                    duration: 0.38,
                    ease: "power2.out",

                    onUpdate: updateActiveMovie,

                    onComplete: () => {
                        updateActiveMovie();
                        setGallerySettled(true);

                        window.setTimeout(() => {
                            suppressClickRef.current = false;
                        }, 120);
                    },
                });

                pointerId = null;
            };

            stage.addEventListener(
                "pointerdown",
                handlePointerDown
            );

            stage.addEventListener(
                "pointermove",
                handlePointerMove
            );

            stage.addEventListener(
                "pointerup",
                finishPointerGesture
            );

            stage.addEventListener(
                "pointercancel",
                finishPointerGesture
            );

            return () => {
                stage.removeEventListener(
                    "pointerdown",
                    handlePointerDown
                );

                stage.removeEventListener(
                    "pointermove",
                    handlePointerMove
                );

                stage.removeEventListener(
                    "pointerup",
                    finishPointerGesture
                );

                stage.removeEventListener(
                    "pointercancel",
                    finishPointerGesture
                );

                snapTween?.kill();
                gsap.killTweensOf(ring);
            };
        });

        return () => {
            mm.revert();
        };
    }, [movies.length]);

    useEffect(() => {
        setSettledMovieId(null);
        setReadyVideoMovieId(null);

        if (
            !activeMovieId ||
            !isGallerySettled
        ) {
            return undefined;
        }

        const settleTimer = setTimeout(() => {
            setSettledMovieId(activeMovieId);
        }, 650);

        return () => {
            clearTimeout(settleTimer);
        };
    }, [activeMovieId, isGallerySettled]);

    useEffect(() => {
        setReadyVideoMovieId(null);

        if (!settledMovieId) {
            setActiveVideo({
                movieId: null,
                key: null,
            });

            return undefined;
        }

        if (videoCacheRef.current.has(settledMovieId)) {
            const cachedKey =
                videoCacheRef.current.get(settledMovieId);

            setActiveVideo({
                movieId: settledMovieId,
                key: cachedKey,
            });

            return undefined;
        }

        const controller = new AbortController();

        setActiveVideo({
            movieId: settledMovieId,
            key: null,
        });

        const fetchActiveMovieVideo = async () => {
            try {
                const response = await axios.get(
                    `https://api.themoviedb.org/3/movie/${settledMovieId}/videos?api_key=e16900047627f56531a2d3d8d4df9e92&language=en-US`,
                    {
                        signal: controller.signal,
                    }
                );

                const videos = Array.isArray(
                    response.data?.results
                )
                    ? response.data.results
                    : [];

                const youtubeVideos = videos.filter(
                    (video) =>
                        video?.site === "YouTube" &&
                        video?.key
                );

                const selectedVideo =
                    youtubeVideos.find(
                        (video) =>
                            video.type === "Trailer" &&
                            video.official === true
                    ) ||
                    youtubeVideos.find(
                        (video) =>
                            video.type === "Trailer"
                    ) ||
                    youtubeVideos.find(
                        (video) =>
                            video.type === "Teaser" &&
                            video.official === true
                    ) ||
                    youtubeVideos[0] ||
                    null;

                const videoKey =
                    selectedVideo?.key || null;

                videoCacheRef.current.set(
                    settledMovieId,
                    videoKey
                );
                if (controller.signal.aborted) return;

                setActiveVideo({
                    movieId: settledMovieId,
                    key: videoKey,
                });
            } catch (error) {
                if (error?.code === "ERR_CANCELED") {
                    return;
                }

                console.error(
                    "[MovieFrenzy] Erreur vidéo SuperStar:",
                    error
                );

                videoCacheRef.current.set(
                    settledMovieId,
                    null
                );

                setActiveVideo({
                    movieId: settledMovieId,
                    key: null,
                });
            }
        };

        fetchActiveMovieVideo();

        return () => {
            controller.abort();
        };
    }, [settledMovieId]);
    if (!movies.length) {
        return null;
    }

    const angleStep = 360 / movies.length;

    const handleActiveVideoReady = (
        event,
        movieId
    ) => {
        if (movieId !== activeMovieId) return;

        event.target.mute();
        event.target.playVideo();

        setReadyVideoMovieId(movieId);
    };

    const handleMovieClick = (movie, index) => {
        if (suppressClickRef.current) return;
        if (index !== activeIndex) return;

        navigate(`/ficheFilm/${movie.id}`);
    };

    const handleMovieKeyDown = (
        event,
        movie,
        index
    ) => {
        if (index !== activeIndex) return;

        if (
            event.key === "Enter" ||
            event.key === " "
        ) {
            event.preventDefault();

            navigate(`/ficheFilm/${movie.id}`);
        }
    };

    const youtubeOptions = {
        width: "100%",
        height: "100%",

        playerVars: {
            autoplay: 1,
            controls: 0,
            playsinline: 1,
            rel: 0,
            enablejsapi: 1,
            origin: window.location.origin,
        },
    };

    return (
        <section
            className="superstar-gallery"
            aria-label={t("actor.superstar360AriaLabel").replace("{{name}}", actorName)}
        // style={{ border: "2px solid rgba(0, 255, 255, 0.45)" }}
        >
            <div className="superstar-gallery__heading">
                <span className="superstar-gallery__eyebrow">
                    MOVIEFRENZY
                </span>

                {regionalScope === "quebec" && (
                    <span className="superstar-gallery__regional-badge">
                        {t("actor.superstarQuebecScope")}

                        <span
                            className="superstar-gallery__regional-symbol"
                            aria-hidden="true"
                        >
                            ⚜︎
                        </span>
                    </span>
                )}

                <h2 className="superstar-gallery__title">
                    {t("actor.superstar360Title")}
                </h2>

                {actorName && (
                    <p className="superstar-gallery__actor">
                        {actorName}
                    </p>
                )}
            </div>

            <div
                ref={stageRef}
                className="superstar-gallery__stage"
                onPointerDown={(event) => {
                    if (event.pointerType !== "mouse") {
                        setHoveredBackdropMovie(null);
                        setFocusedBackdropMovie(null);
                        setTouchBackdropEnabled(true);
                    }
                }}
            >

                {showHeroBackdropCue && (
                    <div
                        className="superstar-gallery__hero-backdrop-cue"
                        aria-hidden="true"
                    >
                        <div className="superstar-gallery__hero-arrow superstar-gallery__hero-arrow--left">
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>

                        <div className="superstar-gallery__hero-arrow superstar-gallery__hero-arrow--right">
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                    </div>
                )}
                <div
                    className={`superstar-gallery__front-glow ${settledMovieId === activeMovieId
                        ? "superstar-gallery__front-glow--visible"
                        : ""
                        }`}
                />
                <div
                    ref={ringRef}
                    className="superstar-gallery__ring"
                >
                    {movies.map((movie, index) => {
                        const angle = index * angleStep;
                        const releaseYear =
                            movie.release_date?.slice(0, 4);
                        const isActive =
                            index === activeIndex;

                        const hasActiveVideo =
                            isActive &&
                            activeVideo.movieId === movie.id &&
                            Boolean(activeVideo.key);

                        const isVideoReady =
                            hasActiveVideo &&
                            readyVideoMovieId === movie.id;

                        return (
                            <div
                                key={movie.id}
                                className="superstar-gallery__slot"
                                style={{
                                    "--superstar-angle": `${angle}deg`,
                                }}
                            >
                                <article
                                    className={`superstar-gallery__movie ${index === activeIndex
                                        ? "superstar-gallery__movie--active"
                                        : ""
                                        }`}
                                    onPointerEnter={(event) => {
                                        if (event.pointerType === "mouse") {
                                            setTouchBackdropEnabled(false);
                                            setHoveredBackdropMovie(movie);
                                        }
                                    }}
                                    onPointerLeave={() => setHoveredBackdropMovie(null)}
                                    onFocus={(event) => {
                                        if (event.currentTarget.matches(":focus-visible")) {
                                            setFocusedBackdropMovie(movie);
                                        }
                                    }}
                                    onBlur={() => setFocusedBackdropMovie(null)}
                                    onClick={() =>
                                        handleMovieClick(movie, index)
                                    }
                                    onKeyDown={(event) =>
                                        handleMovieKeyDown(
                                            event,
                                            movie,
                                            index
                                        )
                                    }
                                    tabIndex={
                                        index === activeIndex ? 0 : -1
                                    }
                                    role={
                                        index === activeIndex
                                            ? "link"
                                            : undefined
                                    }
                                    aria-label={
                                        index === activeIndex
                                            ? `Voir ${movie.title ||
                                            movie.original_title
                                            }`
                                            : undefined
                                    }
                                >
                                    <div className="superstar-gallery__media">

                                        <img
                                            className={`superstar-gallery__poster ${isVideoReady
                                                ? "superstar-gallery__poster--video-ready"
                                                : ""
                                                }`}
                                            src={`https://image.tmdb.org/t/p/w500${movie.poster_path}`}
                                            alt={movie.title || movie.original_title}
                                            draggable="false"
                                        />

                                        {hasActiveVideo && (
                                            <div
                                                className={`superstar-gallery__video ${isVideoReady
                                                    ? "superstar-gallery__video--ready"
                                                    : ""
                                                    }`}
                                            >
                                                <YouTube
                                                    key={`${movie.id}-${activeVideo.key}`}
                                                    videoId={activeVideo.key}
                                                    opts={youtubeOptions}
                                                    className="superstar-gallery__youtube"
                                                    iframeClassName="superstar-gallery__youtube-iframe"
                                                    onReady={(event) =>
                                                        handleActiveVideoReady(
                                                            event,
                                                            movie.id
                                                        )
                                                    }
                                                />
                                            </div>
                                        )}

                                    </div>

                                    <div className="superstar-gallery__movie-info">
                                        <h3>
                                            {movie.title ||
                                                movie.original_title}
                                        </h3>

                                        {releaseYear && (
                                            <span>{releaseYear}</span>
                                        )}
                                    </div>
                                </article>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default SuperStarVideoGallery;